// Public UI adapted from Klargo-Reference/components/landing/HeroDesk.tsx.
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLanguage } from '../layout/LanguageSwitcher.jsx';
import RegistrationPlate from '../common/RegistrationPlate.jsx';
import { ForwardArrow, VerifiedTick } from '../ds/StatusMark.jsx';
import { formatDateLocalized } from '../../lib/format.jsx';
import { HERO_CARD_ORDER, HERO_DEAL, INSURANCE_PROVIDERS } from './heroDeal.jsx';
const SLOTS = ['front', 'back1', 'back2', 'back3'];
const BACK_SLOTS = ['back1', 'back2', 'back3'];
const pileFor = (front) => {
    const behind = HERO_CARD_ORDER.filter((key) => key !== front);
    return { front, back1: behind[0], back2: behind[1], back3: behind[2] };
};
const SLOT_Z = { back3: 1, back2: 2, back1: 3, front: 4 };
const GEOMETRY = {
    pad: 8,
    reveal: 4,
    share: 0.9,
    shareNarrow: 0.92,
    step: [10, 4, 16],
    stepNarrow: [8, 3, 13],
    tilt: [0.8, 1.6, 2.6],
    tiltNarrow: [0.6, 1.2, 2.0],
    narrowBelow: 340,
    shadowRoom: 48,
};
const usePrefersReducedMotion = () => {
    const [reduced, setReduced] = useState(false);
    useEffect(() => {
        if (typeof window.matchMedia !== 'function')
            return;
        const query = window.matchMedia('(prefers-reduced-motion: reduce)');
        const update = () => setReduced(query.matches);
        update();
        query.addEventListener?.('change', update);
        return () => query.removeEventListener?.('change', update);
    }, []);
    return reduced;
};
const DeskSheet = ({ context, chip, aside, children }) => (<div className="hero-desk-sheet flex w-full flex-col overflow-hidden rounded-[12px] border border-ds-rule-edge bg-ds-sheet p-4">
    <header className="flex h-[22px] flex-none items-center justify-between gap-2.5">
      <span className="text-[11.5px] font-semibold tracking-[0.02em] text-ds-muted">{context}</span>
      {chip && (<span className="inline-flex flex-none items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-[3px] text-[11px] font-semibold" style={{ background: 'var(--ds-green-bg)', color: 'var(--ds-green-deep)' }}>
          <VerifiedTick />
          {chip}
        </span>)}
      {aside}
    </header>
    {children}
  </div>);
const RoleLabel = ({ children }) => (<div className="text-[10px] font-semibold uppercase tracking-[0.05em] text-ds-body">{children}</div>);
const DeskContract = () => {
    const { t, languageCode } = useLanguage();
    const signedDate = formatDateLocalized(HERO_DEAL.dateIso, languageCode);
    const parties = [
        { role: t('Säljare'), name: HERO_DEAL.seller },
        { role: t('Köpare'), name: HERO_DEAL.buyer },
    ];
    return (<DeskSheet context={t('Kontrakt')} chip={t('Signerat')}>
      <div data-desk-identity className="mt-1.5 font-ds-serif text-[17px] font-semibold leading-[1.2] text-ds-ink">
        {t('Köpekontrakt')}
      </div>

      
      <div className="mt-2.5 grid flex-none grid-cols-2 gap-x-3 border-t-2 border-ds-ink pt-[6px]">
        {parties.map((party) => (<div key={party.role}>
            <div className="text-[10px] font-semibold tracking-[0.05em] text-ds-muted">
              {party.role.toUpperCase()}
            </div>
            <div className="mt-px truncate text-[12.5px] font-semibold leading-[1.25] text-ds-ink">
              {party.name}
            </div>
          </div>))}
      </div>

      <div className="mt-[6px] flex flex-wrap items-center gap-x-2.5 gap-y-1 border-t border-ds-rule-row pt-[6px]">
        <span className="text-[12px] font-medium text-ds-body">{HERO_DEAL.model}</span>
        <RegistrationPlate value={HERO_DEAL.reg} size="micro"/>
      </div>

      <div className="mt-[6px] flex items-baseline justify-between gap-3 border-t border-ds-rule-row pt-[6px]">
        <span className="text-[11.5px] text-ds-muted">{t('Pris')}</span>
        <span className="text-[13.5px] font-semibold tabular-nums text-ds-ink">{HERO_DEAL.price}</span>
      </div>

      <div className="mt-[6px] border-t border-b border-ds-rule-row py-[5px]">
        <div className="text-[11.5px] font-semibold leading-[1.3] text-ds-ink">
          {t('contract.preview.condition')}
        </div>
        <div className="mt-px text-[11px] leading-[1.4] text-ds-muted">
          {t('contract.preview.condition.body1')}
        </div>
      </div>

      
      <div className="mt-2 grid flex-none grid-cols-2 gap-x-3">
        {parties.map((party) => (<div key={party.role} className="border-t border-[rgba(20,36,46,.42)] pt-[4px]">
            <div className="truncate font-ds-serif text-[12.5px] font-semibold leading-[1.2] text-ds-ink max-[339px]:text-[11.5px]">
              {party.name}
            </div>
            <div className="text-[10px] text-ds-muted">{party.role}</div>
          </div>))}
      </div>
      <div className="mt-[6px] flex flex-none items-center gap-1.5 text-[11px] text-ds-muted">
        <VerifiedTick />
        <span>
          {t('Signerat med BankID')} · {signedDate}
        </span>
      </div>
    </DeskSheet>);
};
const DeskPayment = () => {
    const { t, languageCode } = useLanguage();
    const confirmedDate = formatDateLocalized(HERO_DEAL.dateIso, languageCode);
    return (<DeskSheet context={t('Betalning')}>
      <div className="mt-0.5 flex-none">
        <div data-desk-identity className="font-ds-serif text-[22px] font-semibold leading-[1.1] tracking-[-0.01em] text-ds-ink">
          {HERO_DEAL.price}
        </div>
        <div className="mt-2 text-[11.5px] leading-[1.4] text-ds-muted">{t('Betalt med Swish')}</div>
      </div>

      <div className="mt-3 grid flex-none grid-cols-[auto_auto_auto] items-center justify-start gap-x-3 border-t border-ds-rule-row pt-[9px]">
        <div>
          <RoleLabel>{t('Köpare')}</RoleLabel>
          <div className="mt-px text-[12.5px] font-semibold leading-[1.25] text-ds-ink">{HERO_DEAL.buyer}</div>
        </div>
        <ForwardArrow stroke="var(--ds-muted)" width={14} height={10}/>
        <div>
          <RoleLabel>{t('Säljare')}</RoleLabel>
          <div className="mt-px text-[12.5px] font-semibold leading-[1.25] text-ds-ink">{HERO_DEAL.seller}</div>
        </div>
      </div>

      
      <div className="mt-3 flex items-center gap-1.5 border-t border-ds-rule-row pt-[8px] text-[11px] leading-[1.4]">
        <VerifiedTick />
        <span className="text-ds-body">{t('Bekräftad')}</span>
        <span className="text-ds-muted">· {confirmedDate}</span>
      </div>
      <div className="mt-[7px] border-t border-ds-rule-row pt-[7px]">
        <RoleLabel>{t('Affärsreferens')}</RoleLabel>
        <div className="mt-px font-ds-mono text-[11px] tracking-[0.02em] text-ds-ink">{HERO_DEAL.contractNumber}</div>
      </div>
    </DeskSheet>);
};
const DeskTransfer = () => {
    const { t } = useLanguage();
    return (<DeskSheet context={t('Ägarbyte')} aside={<RegistrationPlate value={HERO_DEAL.reg} size="micro"/>}>
      
      <div data-desk-identity className="mt-1.5 font-ds-serif text-[16px] font-semibold leading-[1.25] text-ds-ink">
        {HERO_DEAL.buyer} {t('blir ny ägare')}
      </div>

      <div className="mt-2.5 border-t-2 border-ds-ink pt-[8px]">
        <div className="text-[12px] leading-[1.4] text-ds-body">{t('Underlaget är förberett')}</div>
        <div className="mt-[3px] text-[11.5px] leading-[1.4] text-ds-muted">{HERO_DEAL.model}</div>
      </div>

      <div className="mt-2.5 border-t border-ds-rule-row pt-[8px]">
        <div className="text-[10px] font-semibold uppercase tracking-[0.05em] text-ds-body">{t('Nästa steg')}</div>
        <div className="mt-[3px] text-[12.5px] leading-[1.45] text-ds-body">
          {t('Du anmäler ägarbytet hos Transportstyrelsen.')}
        </div>
      </div>
    </DeskSheet>);
};
const DeskInsurance = () => {
    const { t } = useLanguage();
    return (<DeskSheet context={t('Försäkring')}>
      <div className="mt-1.5 flex-none">
        <div data-desk-identity className="font-ds-serif text-[17px] font-semibold leading-[1.2] text-ds-ink">
          {t('Jämför försäkring')}
        </div>
        <div className="mt-2 text-[11.5px] leading-[1.4] text-ds-muted">{t('Förmedlas via Zmarta')}</div>
      </div>
      <div className="mt-2.5 flex-none border-t border-ds-rule-row pt-[9px]">
        <div className="mb-2 text-[10px] font-semibold uppercase tracking-[0.05em] text-ds-muted">
          {t('hero.insurers.exampleLabel')}
        </div>
        <div className="space-y-[9px]">
          {INSURANCE_PROVIDERS.map((companyKey) => (<div key={companyKey} className="text-[12.5px] font-semibold leading-[1.3] text-ds-ink">
              {t(companyKey)}
            </div>))}
        </div>
      </div>
    </DeskSheet>);
};
const DeskCard = ({ cardKey }) => {
    switch (cardKey) {
        case 'contract':
            return <DeskContract />;
        case 'payment':
            return <DeskPayment />;
        case 'transfer':
            return <DeskTransfer />;
        case 'insurance':
            return <DeskInsurance />;
        default:
            return null;
    }
};
const slotOf = (slots, key) => SLOTS.find((slot) => slots[slot] === key) ?? 'front';
const EMPTY_MEASURE = {
    contract: { height: 0, identity: 0 },
    payment: { height: 0, identity: 0 },
    transfer: { height: 0, identity: 0 },
    insurance: { height: 0, identity: 0 },
};
const HeroDesk = ({ footer }) => {
    const { t } = useLanguage();
    const reducedMotion = usePrefersReducedMotion();
    const [front, setFront] = useState('contract');
    const slots = pileFor(front);
    const [stageWidth, setStageWidth] = useState(0);
    const [measure, setMeasure] = useState(EMPTY_MEASURE);
    const [ready, setReady] = useState(false);
    const [announcement, setAnnouncement] = useState('');
    const stageRef = useRef(null);
    const sheetRefs = useRef({});
    const labels = {
        contract: t('Kontrakt'),
        payment: t('Betalning'),
        transfer: t('Ägarbyte'),
        insurance: t('Försäkring'),
    };
    useLayoutEffect(() => {
        const stage = stageRef.current;
        if (!stage)
            return;
        const run = () => {
            setStageWidth(stage.clientWidth);
            setMeasure((current) => {
                const next = { ...current };
                let changed = false;
                for (const key of HERO_CARD_ORDER) {
                    const paper = sheetRefs.current[key];
                    const height = paper?.offsetHeight ?? 0;
                    const id = paper?.querySelector('[data-desk-identity]');
                    const identity = id ? id.offsetTop + id.offsetHeight : 0;
                    if (height !== next[key].height || identity !== next[key].identity) {
                        next[key] = { height, identity };
                        changed = true;
                    }
                }
                return changed ? next : current;
            });
        };
        run();
        if (typeof ResizeObserver === 'undefined')
            return;
        const observer = new ResizeObserver(run);
        observer.observe(stage);
        for (const key of HERO_CARD_ORDER) {
            const el = sheetRefs.current[key];
            if (el)
                observer.observe(el);
        }
        return () => observer.disconnect();
    }, []);
    useEffect(() => {
        const id = window.requestAnimationFrame(() => setReady(true));
        return () => window.cancelAnimationFrame(id);
    }, []);
    const narrow = stageWidth > 0 && stageWidth < GEOMETRY.narrowBelow;
    const sheetWidth = stageWidth > 0 ? Math.round(stageWidth * (narrow ? GEOMETRY.shareNarrow : GEOMETRY.share)) : 0;
    const tilt = narrow ? GEOMETRY.tiltNarrow : GEOMETRY.tilt;
    const step = narrow ? GEOMETRY.stepNarrow : GEOMETRY.step;
    const stripOf = (key) => (measure[key].identity > 0 ? measure[key].identity + GEOMETRY.reveal : 0);
    const strips = HERO_CARD_ORDER.map(stripOf);
    const frontY = GEOMETRY.pad + strips.reduce((sum, s) => sum + s, 0) - Math.min(...strips);
    const frontHeight = measure[slots.front].height;
    const frontBottom = frontY + frontHeight;
    const stageHeight = frontBottom;
    const backY = { front: frontY, back1: 0, back2: 0, back3: 0 };
    let cursor = frontY;
    for (const slot of BACK_SLOTS) {
        cursor -= stripOf(slots[slot]);
        backY[slot] = cursor;
    }
    const positionOf = (slot) => {
        if (slot === 'front')
            return { x: 0, y: frontY, r: 0 };
        const index = BACK_SLOTS.indexOf(slot);
        return { x: step[index], y: backY[slot], r: tilt[index] };
    };
    const clipOf = (slot, key) => {
        if (slot === 'front' || stageWidth === 0)
            return -GEOMETRY.shadowRoom;
        const position = positionOf(slot);
        const wedge = Math.ceil((sheetWidth / 2) * Math.sin((Math.abs(position.r) * Math.PI) / 180)) + 1;
        const overflow = position.y + measure[key].height - frontBottom + wedge;
        return Math.max(-GEOMETRY.shadowRoom, overflow);
    };
    const bringForward = (key, viaKeyboard) => {
        if (key === front)
            return;
        setFront(key);
        setAnnouncement(t('hero.desk.live').replace('{name}', labels[key]));
        if (viaKeyboard) {
            window.requestAnimationFrame(() => sheetRefs.current[key]?.focus());
        }
    };
    return (<div className="hero-desk" data-testid="hero-desk" data-ready={ready ? 'true' : 'false'} data-motion={reducedMotion ? 'reduced' : 'full'}>
      
      <div aria-hidden="true" className="hero-desk-wash"/>
      <div ref={stageRef} className="hero-desk-stage" data-testid="hero-desk-stage" style={{ height: stageHeight || undefined, transition: reducedMotion ? 'none' : undefined }}>
        {HERO_CARD_ORDER.map((key) => {
            const slot = slotOf(slots, key);
            const isFront = slot === 'front';
            const position = positionOf(slot);
            return (<div key={key} className="hero-desk-card" data-key={key} data-slot={slot} style={{
                    width: sheetWidth || undefined,
                    '--x': `${position.x}px`,
                    '--y': `${position.y}px`,
                    '--r': `${position.r}deg`,
                    '--z': SLOT_Z[slot],
                    '--clip': `${clipOf(slot, key)}px`,
                    transition: reducedMotion ? 'none' : undefined,
                }}>
              <div ref={(el) => {
                    sheetRefs.current[key] = el;
                }} role={isFront ? 'region' : undefined} aria-label={isFront ? labels[key] : undefined} aria-hidden={isFront ? undefined : true} tabIndex={isFront ? -1 : undefined} className="hero-desk-paper outline-none">
                <DeskCard cardKey={key}/>
              </div>
              {!isFront && (<button type="button" className="hero-desk-hit" aria-label={t(`hero.desk.show.${key}`)} onClick={(event) => bringForward(key, event.detail === 0)}/>)}
            </div>);
        })}
      </div>
      <p className="sr-only" aria-live="polite">
        {announcement}
      </p>
      <p className="hero-desk-caption mb-0 text-[12px] leading-[1.5] text-ds-muted">
        {t('Exempel från en affär i Klargo.')}
      </p>
      {footer}
    </div>);
};
export default HeroDesk;
